import type { CSSProperties } from 'react';
import type { Project, ProjectColor, ProjectPreset } from '@timato/shared';
import { ProjectPreset as ProjectPresetSchema } from '@timato/shared';
import { readableOn } from './contrast';

/** The named colours, in the order they appear in the picker. */
export const PRESET_COLORS = ProjectPresetSchema.options;

export const COLOR_NAME: Record<ProjectPreset, string> = {
  tomato: 'Tomato', blue: 'Blue', green: 'Green', amber: 'Amber', violet: 'Violet',
  lime: 'Lime', teal: 'Teal', sky: 'Sky', pink: 'Pink', slate: 'Slate',
};

export const isPreset = (c: ProjectColor): c is ProjectPreset => !c.startsWith('#');
export const colorLabel = (c: ProjectColor) => (isPreset(c) ? COLOR_NAME[c] : c.toUpperCase());

/**
 * CSS colour for a project. A named colour is a token, so the theme layer swaps it; a custom one is the
 * hex the user picked and is the same in both themes, which is what they asked for when they picked it.
 */
export const colorVar = (c: ProjectColor) => (isPreset(c) ? `var(--color-project-${c})` : c);
/** A project's colour, or the accent when there is no project (spec §3.1 ring rule). */
export const projectColor = (p: Pick<Project, 'color'> | undefined | null) => (p ? colorVar(p.color) : 'var(--color-accent)');

/**
 * Text that reads on a project fill. Named colours defer to the theme tokens, since they differ per theme;
 * a custom colour is one hex, so its text colour follows from how light that hex is.
 */
export const onProjectColor = (c: ProjectColor) =>
  isPreset(c) ? (c === 'amber' ? 'var(--color-on-amber)' : 'var(--color-on-project)') : readableOn(c);

/**
 * The CSS variables the timer's primary pill reads (`tone="timer"` in TimerControls): the project's colour and the
 * text that reads on it, or the timer's own colours with no project. Always both, so the class needs no fallback.
 */
export const timerButtonVars = (p: Pick<Project, 'color'> | null | undefined) => ({
  '--timer-btn': p ? colorVar(p.color) : 'var(--color-timer-btn-bg)',
  '--timer-btn-fg': p ? onProjectColor(p.color) : 'var(--color-timer-btn-fg)',
}) as CSSProperties;
